import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import { dropdownOptions, fetchMiscOptionRows, DROPDOWN_VALUES_CHANGED } from '../../../components/qv4/models/shared/dropdownValuesModel';
import { registerSessionReset, sessionTicket, sessionIsCurrent } from '../../../utils/sessionLifecycle';

const entries = new Map(), listeners = new Set();
const EMPTY = Object.freeze({ rows: [], loading: true, error: null });
let snapshot = new Map();
const notify = () => { snapshot = new Map(entries); listeners.forEach(listener => listener()); };
export function resetMiscOptions(mode) {
  if (mode) entries.delete(mode); else entries.clear();
  notify();
}
registerSessionReset(() => resetMiscOptions());
if (typeof window !== 'undefined') window.addEventListener(DROPDOWN_VALUES_CHANGED, event => resetMiscOptions(event.detail?.mode));
function load(mode, retry = false) {
  if (!mode || (!retry && entries.has(mode))) return;
  const ticket = sessionTicket(), entry = { ...EMPTY };
  entries.set(mode, entry); notify();
  Promise.resolve().then(() => fetchMiscOptionRows(mode)).then(rows => {
    if (entries.get(mode) === entry && sessionIsCurrent(ticket)) { entries.set(mode, { rows, loading: false, error: null }); notify(); }
  }).catch(() => {
    if (entries.get(mode) === entry && sessionIsCurrent(ticket)) { entries.set(mode, { rows: [], loading: false, error: "Couldn't load options" }); notify(); }
  });
}
const subscribe = listener => { listeners.add(listener); return () => listeners.delete(listener); };
export function useMiscOptions(mode, selected, allowed) {
  const ticket = useRef(sessionTicket());
  const current = useSyncExternalStore(subscribe, () => snapshot);
  const state = current.get(mode) || EMPTY;
  useEffect(() => { if (sessionIsCurrent(ticket.current)) load(mode); }, [mode, current]);
  return { ...state, options: dropdownOptions(state.rows, selected, allowed), retry: () => { if (sessionIsCurrent(ticket.current)) load(mode, true); },
    label: value => state.rows.find(row => String(row.value) === String(value))?.label || '—' };
}

// String labels for existing tags, tables and composed descriptions. Domain
// callers name the mode explicitly; ambiguous codes never share a global label.
export function useMiscLabels(modes) {
  const ticket = useRef(sessionTicket()), key = JSON.stringify(modes);
  const current = useSyncExternalStore(subscribe, () => snapshot);
  useEffect(() => { if (sessionIsCurrent(ticket.current)) JSON.parse(key).forEach(mode => load(mode)); }, [key, current]);
  return useCallback((mode, value) => {
    // COD's due trigger is computed by the server, not a selectable option.
    if (mode === 'payment_due_trigger' && value === 'delivery') return 'On delivery';
    return current.get(mode)?.rows.find(row => String(row.value) === String(value))?.label || '—';
  }, [current]);
}
